export const ExportTargetType = {
  USER: "USER",
  GUEST: "GUEST",
} as const;

export type ExportTargetType =
  (typeof ExportTargetType)[keyof typeof ExportTargetType];

export const ExportJobStatus = {
  PENDING: "PENDING",
  RUNNING: "RUNNING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
} as const;

export type ExportJobStatus =
  (typeof ExportJobStatus)[keyof typeof ExportJobStatus];

// Exports cannot be cancelled — terminal is COMPLETED | FAILED only (no CANCELLED).
const TERMINAL_EXPORT_STATUSES = new Set<ExportJobStatus>([
  ExportJobStatus.COMPLETED,
  ExportJobStatus.FAILED,
]);

export const isTerminalExportStatus = (
  status: ExportJobStatus | null | undefined,
): boolean => (status ? TERMINAL_EXPORT_STATUSES.has(status) : false);

export interface ExportProgressStep {
  /** One of `catalog:gather`, `region:gather`, `archive:upload`. */
  step: string;
  status: "done" | "failed";
  count?: number;
  error?: string;
}

export interface ExportSummary {
  counts: Record<string, number>;
  warnings: string[];
  archiveKey: string;
  expiresAt: string;
}

/** Mirrors the backend `ExportJobResponseDto`. */
export interface ExportJob {
  id: string;
  targetType: ExportTargetType;
  targetId: string;
  targetLabel: string | null;
  tenantId: string;
  region: string | null;
  status: ExportJobStatus;
  progress: ExportProgressStep[] | null;
  summary: ExportSummary | null;
  error: string | null;
  requestedById: string;
  requestedByEmail: string | null;
  /** Retention deadline (7 days after completion); null until COMPLETED. */
  expiresAt: string | null;
  /**
   * Time-limited signed URL. Present only on a COMPLETED, non-expired job and
   * freshly minted on every single-job poll — never cache it. `null` on list rows.
   */
  downloadUrl: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

/** Body for the start-export endpoints. */
export interface StartExportDto {
  /**
   * Required for a SUPER_ADMIN guest export (resolves region + verifies
   * ownership). Ignored for HOTEL_ADMIN (token-derived) and the user route.
   */
  tenantId?: string;
}

// The audit list is always returned newest-first (createdAt desc); the endpoint
// exposes no sort params, only these filters + pagination.
export interface ExportJobsQuery {
  status?: ExportJobStatus;
  targetType?: ExportTargetType;
  page?: number;
  limit?: number;
}

export interface PaginatedExportJobs {
  data: ExportJob[];
  total: number;
  page: number;
  limit: number;
}

/** What a screen needs to drive the confirm → start → poll export flow. */
export interface ExportTargetDescriptor {
  type: ExportTargetType;
  /** Path id (guestId / userId). */
  id: string;
  /** Human-readable label shown in the confirm/status UI (email / openId / id). */
  label: string;
  /** Required for a SUPER_ADMIN guest export; omit for HOTEL_ADMIN and user exports. */
  tenantId?: string;
}
